import { makeAutoObservable } from "mobx";
import RootStore  from "mobx-stores/root-store";
import { Api } from "services";
import _ from "lodash";
import { CredicCardsDTO } from "models";

export class CartCheckoutStore {
  private rootStore: any;
  private userApi: Api["userApi"];
  private storeApi: Api["storeApi"];

  constructor(rootStore: any) {
    this.rootStore = rootStore;
    this.userApi = this.rootStore.generalStore.api.userApi;
    this.storeApi = this.rootStore.generalStore.api.storeApi;
    makeAutoObservable(this);
  }

  // OBSERVABLES
  private storesProducts = null; // TODO-A Add model here
  private storeProductsCheckout = null;
  private paymentDetailsSelected = null;
  private newCreditCard = null;
  private saveNewCard: boolean = false;
  public creditCardFormEnabled: boolean = false;
  public isNewCard: boolean = false;

  // ACTIONS
  public changeCollpaseStateCheckoutStore = (storeName: string): void => {
    this.storeProductsCheckout[storeName].collapsed =
      !this.storeProductsCheckout[storeName].collapsed;
  };

  public setNewCreditCardDetails = (values: CredicCardsDTO): void => {
    const cardType = values.number.charAt(0) === '4' ? 1 : 2;
    this.newCreditCard = {...values, cardType };
  };

  public setCreditCardFormEnabled = (isEnabled: boolean): void => {
    this.creditCardFormEnabled = isEnabled;
  };

  public setIsNewCard = (isNew: boolean): void => {
    this.isNewCard = isNew;
  };

  public switchSaveNewCard = () => {
    this.saveNewCard = !this.saveNewCard;
  };

  public selectPaymentDetails = (id: number) => {
    this.paymentDetailsSelected = id;
  };

  private setCartAndCheckoutStore = (stores): void => {
    this.storesProducts = stores;
    // Checkout page collapse menu and items info
    let storeProducts = _.cloneDeep(stores);
    _.forOwn(this.storesProducts, (store, keyStore) => {
      storeProducts[keyStore] = [
        ...storeProducts[keyStore].filter((product) => product.checked),
      ];
    });
    _.forOwn(storeProducts, (store, keyStore) => {
      if (!store.length) {
        delete storeProducts[keyStore];
      } else {
        storeProducts[keyStore] = {
          storeProducts: storeProducts[keyStore],
          products: storeProducts[keyStore].length,
          items: storeProducts[keyStore].reduce(
            (previousValue, currentValue) => {
              return previousValue + currentValue.count;
            },
            0
          ),
          subtotal: storeProducts[keyStore].reduce(
            (previousValue, currentValue) => {
              return previousValue + currentValue.price * currentValue.count;
            },
            0
          ),
          shippingfee: storeProducts[keyStore].reduce(
            (previousValue, currentValue) => {
              return (
                previousValue + currentValue.shipping.price * currentValue.count
              );
            },
            0
          ),
          storeId: storeProducts[keyStore][0].product.storeId,
        };
        storeProducts[keyStore].total =
          storeProducts[keyStore].subtotal +
          storeProducts[keyStore].shippingfee;
        if (!this.storeProductsCheckout) {
          storeProducts[keyStore].collapsed = false;
        }
        else {
          if(this.storeProductsCheckout[keyStore]?.hasOwnProperty('collapsed')) {
            storeProducts[keyStore].collapsed = _.cloneDeep(this.storeProductsCheckout[keyStore].collapsed)
          } else {
            storeProducts[keyStore].collapsed = false;
          }
        }
      }
    });
    this.storeProductsCheckout = storeProducts;
  };

  // COMPUTED
  public get newCreditCardDetails(): CredicCardsDTO {
    return this.newCreditCard;
  }

  public get isNewCardPaymentOptionSelected(): boolean {
    return this.newCreditCard?.id === "";
  }

  public get isSaved(): boolean {
    return this.saveNewCard;
  }

  public get confirmAndPayRestrict(): boolean {
    const { userAddressesInfo } = this.rootStore.accountStore;
    if (
      this.newCreditCard?.number?.length &&
      this.newCreditCard?.name?.length &&
      this.newCreditCard?.scv?.length &&
      this.newCreditCard?.expDate?.length
    ) {
      if (userAddressesInfo?.length) {
        return true;
      }
    }

    return (
      Boolean(this.paymentDetailsSelected) && Boolean(userAddressesInfo?.length)
    );
  }

  public get cartStoreProducts() {
    // TODO-A Add model here
    return this.storesProducts;
  }

  public get cartStoreSelectedProducts() {
    return this.storeProductsCheckout;
  }

  public get orderSummary() {
    let orderSummary = {
      items: 0,
      shippingFee: 0,
      itemsTotal: 0,
      totalToPay: 0,
    };
    const shippingCosts = {};
    
    for (const keyStore in this.storesProducts) {
      const store = this.storesProducts[keyStore];
  
      for (const keyProduct in store) {
        const product = store[keyProduct];
  
        if (product.checked) {
          orderSummary.items += product.count;
          orderSummary.itemsTotal += product.price * product.count;
  
          const shippingMethod = product.shipping.id;
          const shippingPrice = product.shipping.price;
          shippingCosts[shippingMethod] = shippingPrice;
        }
      }
    }
  
    for (const method in shippingCosts) {
      orderSummary.shippingFee += shippingCosts[method];
    }
  
    orderSummary.totalToPay = orderSummary.shippingFee + orderSummary.itemsTotal;
    return orderSummary;
  }

  public get cartStoreProductsSize() {
    // TODO-A Add model here
    let size = 0;
    for (let objStore in this.storesProducts) {
      size = size + _.size(this.storesProducts[objStore]);
    }
    return size;
  }

  // METHODS
  public getCartByUserId = async (userId: number | string): Promise<any> => {
    if(userId) {
    const cartDetails = await this.userApi.getCartByUserId(userId);
    if (!cartDetails) return;
    this.setCartAndCheckoutStore(cartDetails);
    }
  };

  public deleteCartByProductId = async (
    userId: number | string,
    productId: string | number
  ): Promise<any> => {
    const cartDetails = await this.userApi.deleteCartByProductId(
      userId,
      productId
    );
    if (!cartDetails) return;
    this.getCartByUserId(userId);
  };

  public updateCartByProductId = async (
    userId: number | string,
    productDetails: any
  ): Promise<any> => {
    const payloadCart = {
      product: {
        id: productDetails?.product?.id,
      },
      sku: {
        id: productDetails?.sku?.id,
      },
      count: productDetails?.count,
    };

    try {
      const cartDetails = await this.userApi.updateCartByProductId(
        userId,
        productDetails?.id,
        payloadCart
      );
      if (!cartDetails) return;
      this.getCartByUserId(userId);
    } catch (e) {
      console.error(e);
    }
  };

  public checkCartProduct = async (
    userId: number | string,
    productDetails: any
  ): Promise<any> => {
    const checkPayload = {
      ids: [productDetails?.id],
      checked: productDetails.checked,
    };
    const productIdChecked = await this.userApi.setUserCartChecked(
      userId,
      checkPayload
    );
    if (!productIdChecked) return;
    this.getCartByUserId(userId);
  };

  public checkAllProductsByStore = async (
    userId: number | string,
    storeName: string
  ): Promise<any> => {
    let checkedIds = null;
    let checked = null;
    if (
      this.storesProducts[storeName]
        .map((product) => product.checked)
        .every((checkedValue) => checkedValue)
    ) {
      checkedIds = this.setAllCheckedValues(storeName, false);
      checked = false;
    } else {
      checkedIds = this.setAllCheckedValues(storeName, true);
      checked = true;
    }
    const checkPayload = {
      ids: checkedIds,
      checked: checked,
    };
    const productIdChecked = await this.userApi.setUserCartChecked(
      userId,
      checkPayload
    );
    if (!productIdChecked) return;
    this.getCartByUserId(userId);
  };

  public confirmAndPay = async (userId: number | string): Promise<any> => {
    const {
        routingStore: { push },
        accountStore: { userAddressesInfo, accountDetails },
      } = this.rootStore,
      primaryAddress = userAddressesInfo.filter((address) => address.primary),
      notPrimary = userAddressesInfo.filter((address) => !address.primary),
      checkoutAddress = !!primaryAddress.length ? primaryAddress : notPrimary;

    const { showSuccess, showError } = this.rootStore.generalStore;

    const dateToIsoString = (date: string) => {
      let mm = date.slice(0, 2);
      let yy = date.slice(2, 4);
      let expDate = new Date(`${mm}/10/20${yy}`);
      return expDate?.toISOString();
    };

    let checkoutOrderPayload = {
      orders: [],
      user: {
        id: accountDetails?.id,
        firstname: accountDetails?.firstName,
        lastname: accountDetails?.lastName,
        email: accountDetails?.emailAddress,
        phone: accountDetails?.phoneNumber,
        gender: accountDetails?.sex,
        birth: accountDetails?.dateOfBirth,
      },
      userId: userId,
      credit: {
        id: this.paymentDetailsSelected,
        number: this.newCreditCardDetails.number,
        expDate: this.isNewCardPaymentOptionSelected
          ? dateToIsoString(this.newCreditCardDetails.expDate)
          : this.newCreditCardDetails.expDate,
        name: this.newCreditCardDetails.name,
        cvv: this.newCreditCardDetails.scv,
        isSaved: this.saveNewCard,
        cardType: this.newCreditCardDetails.cardType
      },
      shippingAddressId: checkoutAddress[0].shippingAddress.id,
      billingAddressId: checkoutAddress[0].billingAddress.id,
    };

    _.forOwn(this.storeProductsCheckout, (store, keyStore) => {
      checkoutOrderPayload.orders.push({
        shop: store.storeId,
        products: store.storeProducts.map((product) => {
          return {
            id: product.id,
            shippingAddress: checkoutAddress[0].shippingAddress,
            billingAddress: checkoutAddress[0].billingAddress,
          };
        }),
      });
    });

    try {
      await this.storeApi.ordersCheckout(checkoutOrderPayload)
    } catch (error) {
        const msg = JSON.parse(error.message).message
        this.rootStore.generalStore.showError(msg);
        return;
    }

    this.paymentDetailsSelected = null;
    showSuccess("Payment successful");
    this.getCartByUserId(userId);
    push("/stores");
  };

  private setAllCheckedValues = (
    storeName: string,
    checkedValue: boolean
  ): number[] => {
    let checkedIds = [];
    this.storesProducts[storeName].forEach((product) => {
      product.checked = checkedValue;
      checkedIds.push(product?.id);
    });
    return checkedIds;
  };
}
